import { ReactNode, useEffect, useRef, useState } from 'react';
import { Outlet, useParams, useSearchParams } from 'react-router-dom';
import { Dispatch } from '@reduxjs/toolkit';

import { useGetLoginQuery } from '../api-slice'
import { useAppDispatch, useAppSelector } from '../hooks';
import { AfterLoginAction, AppContext, afterLoginActionsSelector, langSelector, 
         loginStatusSelector, mainActions, useCountry } from '../main-slice';
import { LoginPage } from '../../pages/login-page/login-page';
import { appLogin, restoreSession } from '../api-client';
import { UserRolePage } from '../../pages/user-role-page/user-role-page';
import { ChooseLanguageEmbeededPage } from '../../pages/choose-language-page';
import { LoginResponse } from '../types/api-types';
import { basket3DActions } from '../basket-3d-slice';
import { LoadingAnimation } from '../../view/loading-animation/loading-animation';

const LOGGED_USER_STORAGE_KEY = 'login-user';
const LOGGED_SESSION_STORAGE_KEY = 'session-id';

export function Login({ children }: {
    children: ReactNode
}) {
    const dispatch = useAppDispatch();
    const [ searchParams ] = useSearchParams();
    const params = useParams();
    const afterLoginActions = useAppSelector(afterLoginActionsSelector);
    const country = useCountry();
    const language = params.langId;

    const dealer = searchParams.get('P');
    const hasLoggedIn = useRef(false);
    
    const { data, isSuccess } = useGetLoginQuery({ country, dealer, language: language! }, {
        skip: hasLoggedIn.current || country === '*LOADING' || !language
    });

    useEffect(() => {
        dispatch(mainActions.setAppContext());
    }, [])
    
    useEffect(() => {
        if(isSuccess && data) {
            dispatch(mainActions.logIn({
                lang: data.languageid?.toLowerCase(),
                sessionId: data.sessionid,
                userId: data.userid,
                currency: data.currency,
                isAnonymous: true,
                taxesin_excluded: data.taxesin_excluded
            }));
            
            // Set sales info if available in the response
            if (data.customer || data.pricelist || data.pricelistcurrency || data.jobnumber) {
                dispatch(mainActions.setSalesInfo({
                    customer: data.customer,
                    pricelist: data.pricelist,
                    pricelistcurrency: data.pricelistcurrency,
                    jobnumber: data.jobnumber
                }));
            }
            
            doAfterLoginActions(afterLoginActions, dispatch);
            hasLoggedIn.current = true
        }
    }, [isSuccess, data]);

    return children;
}

export function AppLogin({ appContext, allowAnonymous }: {
    appContext: AppContext
    allowAnonymous: boolean
}) {

    const [ searchParams ] = useSearchParams();
    
    const dispatch = useAppDispatch();
    const stateLang = useAppSelector(langSelector);
    const afterLoginActions = useAppSelector(afterLoginActionsSelector);
    const country = useCountry();
    const loginStatus = useAppSelector(loginStatusSelector);
    
    const [ isAnonymous, setAnonymous ] = useState<boolean|undefined>()
    const [ prevSessionId ] = useState(() => {
        return localStorage.getItem(LOGGED_SESSION_STORAGE_KEY);
    });
    const [ language, setLanguage ] = useState<string|undefined>(() => {
        if(searchParams.get('L')) {
            return searchParams.get('L') ?? undefined;
        } else {
            return undefined;
        }
    });

    // TODO use asyncThunks the way they should (extraReducers, .fulfilled, etc.)

    useEffect(() => {
        dispatch(mainActions.setAppContext(appContext));
        if(prevSessionId) {
            dispatch(mainActions.setLoginStatus('retrieving-prev'));
            restoreSession(prevSessionId).then(
                (response) => {
                    if(response.SUCCESS) {
                        const lang = response.languageid?.toLowerCase();
                        dispatch(mainActions.logIn({
                            lang,
                            sessionId: response.sessionid,
                            userId: response.userid,
                            currency: response.currency,
                            allowedCurrencies: response.allowedcurrencies,
                            isAnonymous: false,
                            taxesin_excluded: response.taxesin_excluded
                        }))
                        
                        // Set sales info if available in the response
                        if (response.customer || response.pricelist || response.pricelistcurrency || response.jobnumber) {
                            dispatch(mainActions.setSalesInfo({
                                customer: response.customer,
                                pricelist: response.pricelist,
                                pricelistcurrency: response.pricelistcurrency,
                                jobnumber: response.jobnumber
                            }));
                        }
                        
                        localStorage.setItem(LOGGED_USER_STORAGE_KEY, response.userid);
                        localStorage.setItem(LOGGED_SESSION_STORAGE_KEY, response.sessionid);                        
                        setLanguage(lang);
                        dispatch(mainActions.setLoginStatus('logged'));
                    } else {
                        dispatch(mainActions.setLoginStatus('not-logged'));
                    }
                },
                (err) => {
                    dispatch(mainActions.setLoginStatus('not-logged'));
                }
            )
        } else {
            dispatch(mainActions.setLoginStatus('not-logged'));
        }
    }, [])

    useEffect(() => {
        if(loginStatus === 'not-logged') {
            localStorage.removeItem(LOGGED_SESSION_STORAGE_KEY);
            setLanguage(undefined);
        }
    }, [ loginStatus ])

    const initUserName = () => ( localStorage.getItem(LOGGED_USER_STORAGE_KEY) ?? '' );

    const searchLanguage = searchParams.get('L');
    if(searchLanguage && language !== searchLanguage && stateLang !== searchLanguage) {
        setLanguage(searchLanguage);
    }

    const onLogin = async (user: string, password: string) => {
        try {
            dispatch(mainActions.setLoginStatus('logging-in'));
            const loginResponse = await appLogin(user, password, language, country, 'user');
            const lang = loginResponse.languageid?.toLowerCase();
            dispatch(mainActions.logIn({
                lang,
                sessionId: loginResponse.sessionid,
                userId: loginResponse.userid,
                currency: loginResponse.currency,
                allowedCurrencies: loginResponse.allowedcurrencies,
                isAnonymous: false,
                taxesin_excluded: loginResponse.taxesin_excluded
            }));
            
            // Set sales info if available in the response
            if (loginResponse.customer || loginResponse.pricelist || loginResponse.pricelistcurrency || loginResponse.jobnumber) {
                dispatch(mainActions.setSalesInfo({
                    customer: loginResponse.customer,
                    pricelist: loginResponse.pricelist,
                    pricelistcurrency: loginResponse.pricelistcurrency,
                    jobnumber: loginResponse.jobnumber
                }));
            }
            
            doAfterLoginActions(afterLoginActions, dispatch);
            localStorage.setItem(LOGGED_USER_STORAGE_KEY, loginResponse.userid);
            localStorage.setItem(LOGGED_SESSION_STORAGE_KEY, loginResponse.sessionid);                
            setLanguage(lang);
            dispatch(mainActions.setLoginStatus('logged'));
        } catch(err) {
            dispatch(mainActions.setLoginStatus('failed'));
            localStorage.removeItem(LOGGED_USER_STORAGE_KEY);
        }        
    }

    const languageDisabled = loginStatus === 'logging-in';
    const roleDisabled = loginStatus === 'logging-in';
    const loginDisabled = country === '*LOADING' || loginStatus === 'logging-in';

    const onFailClose = () => {
        dispatch(mainActions.setLoginStatus('not-logged'));
    }

    const onLanguageSelected = (langId:string) => {
        dispatch(mainActions.setLang(langId));
        setLanguage(langId);
    }

    const onConsumer = async () => {
        const dealer = searchParams.get('P');
        setAnonymous(true);
        try {
            dispatch(mainActions.setLoginStatus('logging-in'));
            let loginResponse: LoginResponse;
            if(dealer) {
                loginResponse = await appLogin(dealer, undefined, language, country, 'dealer');
            } else {
                loginResponse = await appLogin(undefined, undefined, language, country, 'anonymous');
            }
            dispatch(mainActions.logIn({
                lang: loginResponse.languageid?.toLowerCase(),
                sessionId: loginResponse.sessionid,
                userId: loginResponse.userid,
                currency: loginResponse.currency,
                allowedCurrencies: loginResponse.allowedcurrencies,
                isAnonymous: true,
                taxesin_excluded: loginResponse.taxesin_excluded
            }));    
            
            // Set sales info if available in the response
            if (loginResponse.customer || loginResponse.pricelist || loginResponse.pricelistcurrency || loginResponse.jobnumber) {
                dispatch(mainActions.setSalesInfo({
                    customer: loginResponse.customer,
                    pricelist: loginResponse.pricelist,
                    pricelistcurrency: loginResponse.pricelistcurrency,
                    jobnumber: loginResponse.jobnumber
                }));
            }
            
            dispatch(mainActions.setLoginStatus('logged'));
        } catch(err) {
            dispatch(mainActions.setLoginStatus('error'));
        }

    }

    const onPartner = () => {
        setAnonymous(false);
    }


    if(loginStatus === 'initial' || loginStatus === 'retrieving-prev') {
        return <LoadingAnimation/>;
    } else if(!language) {
        return <ChooseLanguageEmbeededPage onLanguageSelected={onLanguageSelected} disabled={roleDisabled}/>
    } else if(loginStatus !== 'logged') {
        if(allowAnonymous && isAnonymous === undefined) {
            return <UserRolePage lang={language} onConsumer={onConsumer} onPartner={onPartner} disabled={roleDisabled}/>
        } else if(!isAnonymous) {
            return <LoginPage onLogin={onLogin} lang={language} failed={loginStatus === 'failed'} disabled={loginDisabled} onFailClose={onFailClose} initUserName={initUserName}/>
        } else {
            return <Outlet />
        }
        
    } else {
        return <Outlet/>;
    }
}

function doAfterLoginActions(actions: AfterLoginAction[], dispatch: Dispatch) {
    for(const action of actions) {
        switch(action.type) {
            case 'loadCart3D':
                // @ts-expect-error
                dispatch(basket3DActions.loadCart3D(action.cartId));
                break;
        }
    }
    dispatch(mainActions.clearAfterLoginActions());
}